/**
 * The error-reporting POLICY — shared by every runtime that talks to GlitchTip
 * (the api edge, the jobs worker, the Astro marketing SSR server, and both
 * SolidJS SPAs). Pure TypeScript with no SDK dependency, so the one thing that
 * must be identical everywhere — WHAT LEAVES THE BOX — is written once and
 * tested once, while each runtime keeps its own thin `Sentry.init(...)` against
 * the SDK flavour it actually runs (`@sentry/bun` vs `@sentry/browser`).
 *
 * Why a policy and not just `Sentry.init({ dsn })`: this is an adults-only
 * companion app. A request body here is a chat message, a persona, or a
 * generation prompt; a cookie is a live session; a header carries the bearer.
 * `compliance.md` § "Minimal PII" makes shipping any of that to an error tracker
 * a compliance incident, and the SDK defaults are permissive enough to do it
 * (breadcrumbs from `console.*`, local variables captured off stack frames,
 * request bodies attached by framework integrations). So the default here is
 * DENY: {@link scrubEvent} strips the payload down to the fault itself, and the
 * per-runtime init lists its integrations explicitly instead of inheriting a
 * set that grows with every SDK upgrade.
 *
 * WHERE THIS POLICY STOPS, stated plainly so nobody mistakes it for total:
 *
 *  - The error's OWN `message` (and therefore `exception.values[].value`) is not
 *    rewritten. It is authored by us, and it is already the thing this app
 *    writes to its pod logs on every fault — GlitchTip is self-hosted on the same
 *    infrastructure, so reporting it widens nothing. What the policy does refuse
 *    is to widen it FURTHER: `linkedErrorsIntegration` is deliberately NOT
 *    installed by any runtime, because it walks `Error.cause` and appends each
 *    link as its own exception — and `cause` is exactly where this codebase puts
 *    the raw thing it declined to author (a provider's own error text, ffmpeg's
 *    stderr, the postgres driver error carrying the offending literal). Logs get
 *    the message and the stack; the tracker gets no more than that.
 *  - It follows that an `AppError` message is a PUBLIC string by this repo's
 *    convention: never interpolate a chat message, a persona, a prompt or a
 *    credential into one. `packages/shared/src/errors/public-view.ts` already
 *    assumes as much for the >= 500 case.
 */

import { AppError } from './errors/base.ts';

/**
 * The reporting seam every app injects (DIP). One method, so a composition root
 * can hand a route/worker the real GlitchTip client, {@link noopErrorReporter},
 * or a test spy without any of them importing an SDK.
 */
export interface ErrorReporter {
  /**
   * Report a fault. MUST NOT throw and MUST NOT block the caller — reporting is
   * best effort by contract, and an error tracker that can take a request path
   * down with it is worse than no error tracker.
   */
  capture(error: unknown, context?: Readonly<Record<string, unknown>>): void;
}

/** The reporter used when no DSN is configured (local dev, tests). Reports nothing. */
export const noopErrorReporter: ErrorReporter = {
  capture: () => {},
};

/**
 * Should this error reach GlitchTip at all?
 *
 * An `AppError` under `500` is a BUSINESS OUTCOME, not a fault: `402` over
 * quota, `403` under age, `422` blocked by the moderation gate, `429` limited.
 * They are the app working. Reporting them buries the real faults under the
 * app's normal traffic and burns the error budget the on-call signal depends on
 * — the same reasoning the edge's error handler already applies when it logs
 * them at `warn` instead of `error`.
 *
 * Everything else reports: an `AppError` at `>= 500` IS a server fault (an
 * upstream that failed, a config error reached at runtime), and a non-`AppError`
 * is by definition unexpected — this repo's rule is that every deliberate
 * failure is an `AppError`, so a bare `Error`/`TypeError` reaching here is
 * precisely the thing worth paging on.
 */
export function shouldReportError(error: unknown): boolean {
  return error instanceof AppError ? error.statusCode >= 500 : true;
}

/** The request block of an event, typed by what this policy touches. */
export interface ScrubbableRequest {
  url?: string;
  method?: string;
  data?: unknown;
  query_string?: unknown;
  cookies?: unknown;
  env?: unknown;
  headers?: unknown;
}

/** One breadcrumb, typed by what this policy touches. */
export interface ScrubbableBreadcrumb {
  category?: string;
  message?: string;
  data?: { [key: string]: unknown };
}

/**
 * The subset of a Sentry/GlitchTip event this policy rewrites. Structurally
 * compatible with `@sentry/core`'s `Event`, so a runtime passes its own
 * `ErrorEvent` straight in with no cast.
 */
export interface ScrubbableEvent {
  request?: ScrubbableRequest;
  user?: { [key: string]: unknown };
  breadcrumbs?: ScrubbableBreadcrumb[];
}

/**
 * Breadcrumb `data` keys kept as-is. Everything else is dropped rather than
 * inspected: breadcrumb payloads are open-ended (the console integration puts
 * the formatted log line in there, ours carry `err.message` and request
 * context), so an allowlist is the only shape that stays safe as the SDK and
 * this app both change.
 */
const SAFE_BREADCRUMB_DATA_KEYS = new Set(['method', 'status_code', 'reason', 'type']);

/**
 * Breadcrumb `data` keys holding a URL — kept, but query-stripped by
 * {@link stripUrlDetail}, because a navigation trail is most of a breadcrumb's
 * debugging value and a path is not a secret here (ids are opaque UUIDs).
 */
const URL_BREADCRUMB_DATA_KEYS = new Set(['url', 'to', 'from']);

/** Bound on any string this policy lets through. Long enough to read, short enough not to smuggle a transcript. */
export const MAX_BREADCRUMB_MESSAGE_LENGTH = 200;

/**
 * Drop the query string and fragment from a URL, keeping origin + path.
 *
 * String surgery rather than `new URL(...)`: the value may be relative
 * (`/conversations/x?y`), may be a non-URL the SDK put there anyway, and must
 * never throw inside `beforeSend` — a throwing `beforeSend` drops the event
 * silently, which is the one outcome worse than an over-scrubbed one. Both
 * separators are cut at the FIRST occurrence of either, since `#a?b` puts the
 * query inside the fragment.
 */
export function stripUrlDetail(url: string): string {
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

/** Truncate to `max` characters, marking the cut so a reader knows it happened. */
function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max)}…`;
}

/** Apply the allowlist above to one breadcrumb's `data` bag. */
function scrubBreadcrumbData(data: { [key: string]: unknown }): { [key: string]: unknown } {
  const kept: { [key: string]: unknown } = {};
  for (const [key, value] of Object.entries(data)) {
    if (URL_BREADCRUMB_DATA_KEYS.has(key) && typeof value === 'string') {
      kept[key] = stripUrlDetail(value);
    } else if (SAFE_BREADCRUMB_DATA_KEYS.has(key)) {
      kept[key] = value;
    }
  }
  return kept;
}

/**
 * Rewrite an event IN PLACE so nothing user-identifying or user-authored leaves
 * the process, then hand it back to the SDK. Mutating (rather than rebuilding)
 * is deliberate: the event carries many fields this policy has no opinion about
 * — the exception chain, the stack, the runtime context — and a rebuild would
 * silently drop whichever ones a future SDK version adds.
 *
 * What goes, and why:
 *   - `request.data`   — the BODY. On this app that is the chat message, the
 *                        persona, the generation prompt, the password.
 *   - `request.cookies`— the live session + refresh cookies. A leaked error
 *                        report would be a session-hijack primitive.
 *   - `request.headers`— `Authorization` (bearer), `Cookie`, the hCaptcha
 *                        token, and the trusted geo headers the 18+ gate reads.
 *   - `request.env`    — the SERVING POD's environment: every secret it booted with.
 *   - the query string — `?ref=`, and any future token-in-URL.
 *   - `user`           — reduced to `id`. An account id is opaque and is the
 *                        whole point (which account hit this); an email,
 *                        username or IP is PII with no debugging value here.
 *   - breadcrumb text  — bounded, and `data` allowlisted (see above).
 */
export function scrubEvent(event: ScrubbableEvent): void {
  const request = event.request;
  if (request) {
    request.data = undefined;
    request.cookies = undefined;
    request.headers = undefined;
    request.env = undefined;
    request.query_string = undefined;
    if (typeof request.url === 'string') {
      request.url = stripUrlDetail(request.url);
    }
  }

  const user = event.user;
  if (user) {
    // Rebuilt, not pruned: `User` is an open bag (`[key: string]: any`), so an
    // allowlist is the only way to be sure a field added upstream — or by a
    // future `setUser` call site here — does not ride along.
    const id = user.id;
    event.user = typeof id === 'string' || typeof id === 'number' ? { id } : undefined;
  }

  const breadcrumbs = event.breadcrumbs;
  if (breadcrumbs) {
    for (const crumb of breadcrumbs) {
      if (typeof crumb.message === 'string') {
        crumb.message = truncate(crumb.message, MAX_BREADCRUMB_MESSAGE_LENGTH);
      }
      if (crumb.data) {
        crumb.data = scrubBreadcrumbData(crumb.data);
      }
    }
  }
}

/** The SDK's capture hint, typed by the one field this policy reads. */
export interface ReportEventHint {
  /** The value originally passed to `captureException` — `undefined` for a message event. */
  originalException?: unknown;
}

/**
 * THE `beforeSend` every runtime installs. It is the last gate before an event
 * leaves the process, and it does the two things that must be true everywhere:
 *
 *  1. DROP what should never have been reported. {@link shouldReportError} also
 *     runs inside each runtime's `capture(...)`, but that is not enough on its
 *     own: the SDK's own global handlers (`onUncaughtException`,
 *     `onUnhandledRejection`, the browser's `globalHandlers`) capture STRAIGHT
 *     THROUGH the client and never touch our reporter. Those handlers are in
 *     fact the ONLY reporting path in the two SPAs — nothing there calls
 *     `capture` — so without this line a `402` over-quota or a `422` blocked by
 *     moderation that escaped as an unhandled rejection was reported like a
 *     fault. Putting the filter here makes it universal by construction.
 *  2. SCRUB what does go, via {@link scrubEvent}.
 *
 * Written as one exported function rather than five inline closures so the
 * compliance-critical callback cannot drift between runtimes, and so a test can
 * drive it directly.
 *
 * It must NEVER throw: a throwing `beforeSend` makes the SDK drop the event
 * silently, which is the one outcome worse than an over-scrubbed one.
 */
export function screenReportEvent<E extends ScrubbableEvent>(
  event: E,
  hint?: ReportEventHint,
): E | null {
  const original = hint?.originalException;
  // Only an event that HAS an originating exception can be judged; a message
  // event (or a hint the SDK did not populate) is reported as-is.
  if (original !== undefined && !shouldReportError(original)) {
    return null;
  }
  scrubEvent(event);
  return event;
}

/**
 * The SDK options every runtime shares. Returned as a plain object each runtime
 * spreads into its own `Sentry.init(...)`, so the compliance-relevant flags
 * cannot drift between five call sites.
 *
 * `tracesSampleRate` is deliberately ABSENT rather than `0`: leaving it unset is
 * what keeps the SDK's performance/OpenTelemetry machinery off entirely. GlitchTip
 * is an error tracker, this app has no tracing budget, and the auto-instrumentation
 * that would come with it reaches straight into the two places PII lives (SQL
 * statements, outbound request bodies).
 */
export interface BaseReportingOptions {
  readonly dsn: string;
  readonly environment: string;
  /**
   * `false`, always. This is the flag that decides whether the SDK attaches the
   * caller's IP, cookies and headers on its own initiative; {@link scrubEvent}
   * is the second line, not the first.
   */
  readonly sendDefaultPii: false;
  /** Bounded so an event cannot carry a long tail of history off the box. */
  readonly maxBreadcrumbs: number;
  /** Depth at which nested structures are stringified. Shallow keeps payloads small. */
  readonly normalizeDepth: number;
}

/** How many breadcrumbs an event may carry (SDK default is 100). */
export const MAX_BREADCRUMBS = 30;

/** Build the shared option set for one runtime. */
export function baseReportingOptions(dsn: string, environment: string): BaseReportingOptions {
  return {
    dsn,
    environment,
    sendDefaultPii: false,
    maxBreadcrumbs: MAX_BREADCRUMBS,
    normalizeDepth: 3,
  };
}

/**
 * Normalize a configured DSN into "report" or "do not report".
 *
 * Blank is absent, not malformed — the same reading `ROUTER_API_KEY` takes. A
 * `.env` line, a ConfigMap key or a shell that exports the variable
 * unconditionally all produce `''`, and off-production "not configured" is a
 * supported, zero-configuration state (`GLITCHTIP_DSN` is documented as
 * optional). Treating `''` as a DSN hands the SDK a value it cannot parse.
 */
export function resolveDsn(raw: string | undefined | null): string | undefined {
  if (typeof raw !== 'string') {
    return undefined;
  }
  const trimmed = raw.trim();
  return trimmed === '' ? undefined : trimmed;
}
